import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { removedRedirectFor, REMOVED_AIRPORT_SLUGS } from "@/data/removedPages";
import { cityRoutePath } from "@/lib/cityRoutes";
import Navbar from "@/components/Navbar";
import CityHeroShared from "@/components/CityHeroShared";
import CityMap from "@/components/CityMap";
import MobileBookNowFab from "@/components/MobileBookNowFab";
import VehicleTypeIcons from "@/components/VehicleTypeIcons";
import SprinterSpecsSection from "@/components/SprinterSpecsSection";
import SeoLongform from "@/components/SeoLongform";
import FleetMeatSection, { ReviewsSection } from "@/components/FleetMeatSection";
import CityUseCases from "@/components/CityUseCases";
import { Phone, Plane, MapPin, Clock, Users, CheckCircle2 } from "lucide-react";
import { AIRPORT_SEO, AIRPORT_LIST, airportHeadMeta, type AirportSeo } from "@/data/airportSeo";
import { ORLANDO_REVIEWS } from "@/data/reviews/orlando";
import { LA_REVIEWS } from "@/data/reviews/la";
import { OC_REVIEWS } from "@/data/reviews/oc";
import { SAN_DIEGO_REVIEWS } from "@/data/reviews/san_diego";
import { SAN_FRANCISCO_REVIEWS } from "@/data/reviews/san_francisco";
import { VEGAS_REVIEWS } from "@/data/reviews/vegas";
import { PHOENIX_REVIEWS } from "@/data/reviews/phoenix";
import { ATLANTA_REVIEWS } from "@/data/reviews/atlanta";
import { CHICAGO_REVIEWS } from "@/data/reviews/chicago";
import { NASHVILLE_REVIEWS } from "@/data/reviews/nashville";
import { NEW_YORK_REVIEWS } from "@/data/reviews/new_york";

import orlandoImg from "@/assets/landmark-orlando.jpg";
import laImg from "@/assets/landmark-la.jpg";
import ocImg from "@/assets/landmark-orangecounty.jpg";
import sdImg from "@/assets/landmark-sandiego.jpg";
import sfImg from "@/assets/landmark-sanfrancisco.jpg";
import vegasImg from "@/assets/landmark-vegas.jpg";
import phxImg from "@/assets/landmark-phoenix.jpg";
import atlImg from "@/assets/landmark-atlanta.jpg";
import chiImg from "@/assets/landmark-chicago.jpg";
import bnaImg from "@/assets/landmark-nashville.jpg";
import nyImg from "@/assets/landmark-newyork.jpg";

import GeoAnswerBlock from "@/components/GeoAnswerBlock";
const CITY_ACCENT: Record<string, { accent: string; accentSecondary: string; bg: string; image: string; reviews: any[] }> = {
  orlando:      { accent: "#7C3AED", accentSecondary: "#FFB347", bg: "#FFF8E7", image: orlandoImg, reviews: ORLANDO_REVIEWS },
  losangeles:   { accent: "#FF2D87", accentSecondary: "#FFB347", bg: "#FFF1F5", image: laImg,      reviews: LA_REVIEWS },
  orangecounty: { accent: "#0EA5E9", accentSecondary: "#FFB347", bg: "#F0F9FF", image: ocImg,      reviews: OC_REVIEWS },
  sandiego:     { accent: "#06B6D4", accentSecondary: "#FFB347", bg: "#ECFEFF", image: sdImg,      reviews: SAN_DIEGO_REVIEWS },
  sanfrancisco: { accent: "#FF5A36", accentSecondary: "#FFB347", bg: "#FFF7F4", image: sfImg,      reviews: SAN_FRANCISCO_REVIEWS },
  vegas:        { accent: "#F59E0B", accentSecondary: "#FFB347", bg: "#FFFBEB", image: vegasImg,   reviews: VEGAS_REVIEWS },
  phoenix:      { accent: "#DC2626", accentSecondary: "#FFB347", bg: "#FEF2F2", image: phxImg,     reviews: PHOENIX_REVIEWS },
  atlanta:      { accent: "#10B981", accentSecondary: "#FFB347", bg: "#ECFDF5", image: atlImg,     reviews: ATLANTA_REVIEWS },
  chicago:      { accent: "#3FA9F5", accentSecondary: "#FFB347", bg: "#EFF6FF", image: chiImg,     reviews: CHICAGO_REVIEWS },
  nashville:    { accent: "#E94B6B", accentSecondary: "#FFB347", bg: "#FFF1F2", image: bnaImg,     reviews: NASHVILLE_REVIEWS },
  newyork:      { accent: "#FACC15", accentSecondary: "#FFB347", bg: "#FEFCE8", image: nyImg,      reviews: NEW_YORK_REVIEWS },
};

export const Route = createFileRoute("/airports/$slug")({
  beforeLoad: ({ params }) => {
    const moved = removedRedirectFor(`/airports/${params.slug}`);
    if (moved) throw redirect({ href: moved, statusCode: 301 });
    if (!AIRPORT_SEO[params.slug]) throw notFound();
  },
  head: ({ params }) => {
    const a = AIRPORT_SEO[params.slug];
    if (!a) {
      return {
        meta: [{ title: "Airport Van Rental — Sprinter Van Rentals" }],
      };
    }
    const cityAccent = CITY_ACCENT[a.citySlug];
    const image = cityAccent?.image;
    // Airport titles must not repeat the city page's title — Search Console
    // flags those as duplicates. Lead with the IATA code + "Airport".
    const title = `${a.code} Airport Sprinter Van Rentals — ${a.shortCity} 12 & 15 Seat`;

    const url = `https://sprinter-van-rentals.com/airports/${a.slug}`;
    return {
      meta: [
        ...airportHeadMeta(a.slug, title),
        ...(image ? [
          { property: "og:image", content: "https://sprinter-van-rentals.com/__l5e/assets-v1/d9d070c6-6676-46c4-b0f4-9b0498e3c905/van-lot-og.jpg" },
          { property: "twitter:image", content: "https://sprinter-van-rentals.com/__l5e/assets-v1/d9d070c6-6676-46c4-b0f4-9b0498e3c905/van-lot-og.jpg" },
        ] : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `${a.code} Airport Passenger Van Rental`,
            serviceType: "Airport passenger van rental",
            provider: {
              "@type": "Organization",
              name: "Sprinter Van Rentals",
              url: "https://sprinter-van-rentals.com",
              telephone: "+1-971-328-2665",
            },
            areaServed: {
              "@type": "Airport",
              name: a.name,
              iataCode: a.code,
            },
            url,
            description: a.slug === "phoenix-phx" ? `Compare passenger van rental options for arrivals at ${a.name} (${a.code}); confirm pickup arrangements and fees before travel.` : `12 and 15 passenger Mercedes Sprinter, Ford Transit, and Chevy Express vans delivered curbside at ${a.name} (${a.code}).`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sprinter-van-rentals.com/" },
              { "@type": "ListItem", position: 2, name: "Airports", item: "https://sprinter-van-rentals.com/airports" },
              { "@type": "ListItem", position: 3, name: `${a.code} — ${a.shortCity}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AirportLanding,
});

function AirportLanding() {
  const { slug } = Route.useParams();
  const airport = AIRPORT_SEO[slug] as AirportSeo;
  const theme = CITY_ACCENT[airport.citySlug] ?? CITY_ACCENT.orlando;
  const accent = "#F97316";
  const isPhx = slug === "phoenix-phx";

  return (
    <main className="min-h-screen bg-white text-[#0F172A]">
      <Navbar />
      <MobileBookNowFab />

      <CityHeroShared
        citySlug={airport.citySlug}
        cityLabel={airport.shortCity}
        badge={`${airport.code} · ${airport.shortCity}, ${airport.state}`}
        headline={`${airport.code} Airport\nVan Rentals`}
        intro={isPhx ? "Compare Phoenix passenger van rentals for your arrival at Phoenix Sky Harbor. Confirm pickup instructions and any fees before travel." : `12 and 15 passenger Sprinter, Transit, and Chevy Express vans delivered curbside at ${airport.name}.`}
        hubs={isPhx ? "PHX pickup arrangements — confirm before travel" : `${airport.code} curbside delivery`}
      />

      <section id="fleet" className="max-w-[1440px] mx-auto px-6 pt-12 md:pt-16 pb-16 scroll-mt-24">
        <div className="mb-10">
          <p className="font-display font-bold text-xs uppercase tracking-[0.4em] mb-3" style={{ color: accent }}>
            The {airport.shortCity} Fleet
          </p>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight text-[#0F172A]">
            Available vans at {airport.code}.
          </h2>
          <p className="mt-3 max-w-2xl text-[#0F172A]/70">
            Same fleet and same pricing as our <Link to={cityRoutePath(airport.citySlug)} className="font-semibold underline underline-offset-4 hover:text-primary">{airport.shortCity} Sprinter van rentals</Link> location{isPhx ? ". Confirm your PHX pickup arrangement and any fees before travel." : " — just delivered to the terminal."}
          </p>
        </div>
        <VehicleTypeIcons accent={accent} citySlug={airport.citySlug as never} />
      </section>

      <section className="max-w-[1440px] mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-[#0F172A]/10 bg-white p-6 shadow-sm space-y-4">
            <div className="flex items-start gap-3">
              <Plane size={18} style={{ color: accent }} className="mt-0.5" />
              <div>
                <p className="font-display text-[11px] uppercase tracking-wider text-[#0F172A]/50">Airport</p>
                <p className="font-semibold">{airport.name} ({airport.code})</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} style={{ color: accent }} className="mt-0.5" />
              <div>
                <p className="font-display text-[11px] uppercase tracking-wider text-[#0F172A]/50">Service area</p>
                <p className="font-semibold">{airport.shortCity}, {airport.state} · {airport.region}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={18} style={{ color: accent }} className="mt-0.5" />
              <div>
                <p className="font-display text-[11px] uppercase tracking-wider text-[#0F172A]/50">Drive time</p>
                <p className="font-semibold">{airport.driveToDowntown}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users size={18} style={{ color: accent }} className="mt-0.5" />
              <div>
                <p className="font-display text-[11px] uppercase tracking-wider text-[#0F172A]/50">Capacity</p>
                <p className="font-semibold">12 & 15 passenger vans</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-[#0F172A]/10 bg-white p-6 shadow-sm">
            <h3 className="font-display font-extrabold text-xl mb-4">
              {isPhx ? "Plan your PHX arrival" : <>We pick up at every {airport.code} terminal</>}
            </h3>
            <ul className="space-y-2 text-sm text-[#0F172A]/80">
              {airport.terminals.map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 size={16} style={{ color: accent }} />
                  {t}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#0F172A]/60 mt-4">
              Tell us your terminal, airline, and flight number when you book — your driver
              meets you curbside at the passenger pickup zone.
            </p>
          </div>

          <div className="rounded-2xl border border-[#0F172A]/10 bg-white p-6 shadow-sm">
            <h3 className="font-display font-extrabold text-xl mb-4">
              Popular destinations from {airport.code}
            </h3>
            <div className="flex flex-wrap gap-2">
              {airport.destinations.map((d) => (
                <span
                  key={d}
                  className="text-xs px-3 py-1.5 rounded-full border"
                  style={{ borderColor: `${accent}40`, backgroundColor: `${accent}10` }}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 pb-12">
        <div className="rounded-2xl border border-[#0F172A]/10 bg-white p-6 md:p-8 shadow-sm">
          <p className="font-display font-bold text-xs uppercase tracking-[0.4em] mb-3" style={{ color: accent }}>
            {airport.name}
          </p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-4">
            The easy way out of {airport.code}.
          </h2>
          <p className="text-[#0F172A]/80 leading-relaxed max-w-4xl">{airport.intro}</p>
          <p className="text-[#0F172A]/70 leading-relaxed max-w-4xl mt-3">
            {airport.driveToDowntown} Whether you're traveling for a wedding, a corporate offsite,
            a sports trip, or a family vacation, one passenger van beats stacking three rideshares —
            for budget, sanity, and luggage.
          </p>
        </div>
      </section>

      <CityMap citySlug={airport.citySlug as never} accent={accent} />

      <ReviewsSection
        city={airport.citySlug}
        accent={accent}
        dark
        accentSecondary={theme.accentSecondary}
        reviewCity={airport.shortCity}
        reviews={theme.reviews}
      />

      <SprinterSpecsSection city={airport.citySlug} accent={accent} />

      <FleetMeatSection
        city={airport.citySlug}
        accent={accent}
        dark
        accentSecondary={theme.accentSecondary}
        reviewCity={airport.shortCity}
      />

      <CityUseCases citySlug={airport.citySlug as never} accent={accent} />

      <SeoLongform citySlug={airport.citySlug} />

      <section className="max-w-[1440px] mx-auto px-6 py-16">
        <h2 className="font-display font-black text-3xl md:text-4xl mb-8">
          {airport.code} Van Rental FAQ
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            {
              q: `Can you deliver a passenger van directly to ${airport.code}?`,
              a: isPhx ? "Confirm the available PHX pickup or delivery arrangement with the reservations team, including the meeting point, timing and any fees. A terminal curbside handover is not automatically included." : `Yes. We deliver 12 and 15 passenger Sprinter, Transit, and Chevy Express vans curbside at ${airport.name}. Share your terminal, airline, and flight number at booking — we meet you at the passenger pickup zone.`,
            },
            {
              q: `Which vans seat the most people at ${airport.code}?`,
              a: isPhx ? "Compare the listed 12- and 15-seat configurations, including the driver in your count. Check the specific rear storage area against your bag sizes before selecting a van." : `Our 15 passenger Ford Transit and Chevy Express vans are the largest. The Mercedes Sprinter seats up to 12 with extra luggage room — popular for crews flying in with gear, golf bags, or wedding decor.`,
            },
            {
              q: `Is ${airport.code} pickup more expensive than picking up at the city location?`,
              a: isPhx ? "Use the current quote for your dates, vehicle and selected pickup arrangement. Confirm any airport delivery fee before booking rather than assuming free terminal handover." : `Pricing matches our ${airport.shortCity} location. An airport delivery fee may apply based on distance — you'll see the exact total before you confirm. No surprise fees at the curb.`,
            },
            {
              q: `What if my flight is delayed?`,
              a: isPhx ? "Contact the reservations team when your arrival changes and confirm the revised meeting time and any applicable terms. Do not assume automatic flight tracking or free delay coverage." : `We track flights for every airport reservation. If your flight slips, your van waits — no extra charge for normal delays.`,
            },
          ].map((f) => (
            <div key={f.q} className="rounded-xl border border-[#0F172A]/10 bg-white p-5 shadow-sm">
              <h3 className="font-display font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-[#0F172A]/75 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GeoAnswerBlock citySlug={airport.citySlug} />

      <section className="max-w-[1440px] mx-auto px-6 pb-20">
        <p className="font-display font-bold text-xs uppercase tracking-[0.4em] mb-3 text-[#0F172A]/60">
          Other airport locations
        </p>
        <div className="flex flex-wrap gap-2">
          {AIRPORT_LIST.filter(
            (x) => x.slug !== airport.slug && x.citySlug === airport.citySlug && !REMOVED_AIRPORT_SLUGS.has(x.slug),
          ).slice(0, 8).map((x) => (
            <Link
              key={x.slug}
              to="/airports/$slug"
              params={{ slug: x.slug }}
              className="text-sm px-4 py-2 rounded-full border border-[#0F172A]/15 bg-white hover:border-[#0F172A]/40 transition-colors"
            >
              {x.code} · {x.shortCity}
            </Link>
          ))}
        </div>
      </section>

      <footer className="py-8 text-center border-t border-[#0F172A]/8">
        <p className="text-[#0F172A]/50 text-xs tracking-widest uppercase">
          &copy; 2026 Sprinter Van Rentals · {airport.code} {airport.shortCity}
        </p>
      </footer>
    </main>
  );
}
